/**
 * Dynamic password validation based on policy.
 * @param {string} password - The password to validate.
 * @param {object} policy - The policy object from backend.
 * @returns {object} - Validation results { score, strength, requirements, isValid, errorMessage }
 */

const isTrue = (val) => {
  if (val === undefined || val === null) return false;
  if (typeof val === 'boolean') return val;
  const s = String(val).toLowerCase();
  return s === 'true' || s === '1' || s === 'on' || s === 'yes';
};

export const validatePassword = (password = '', policy) => {
  const safePassword = String(password || '');
  
  // If policy is not yet loaded, return a pending state to avoid false "Strong" defaults
  if (!policy) {
    return { score: 0, strength: "Loading...", requirements: [], isValid: false, errorMessage: null };
  }

  // Resolve policy values strictly from the fetched configuration only
  const p = {
    min_length: parseInt(policy.min_length || policy.password_min_length || policy.minLength || 0, 10) || 0,
    require_uppercase: isTrue(policy.require_uppercase || policy.requires_uppercase || policy.password_requires_uppercase || policy.requires_upper || policy.require_upper),
    require_lowercase: isTrue(policy.require_lowercase || policy.requires_lowercase || policy.password_requires_lowercase || policy.requires_lower || policy.require_lower),
    require_number: isTrue(policy.require_number || policy.requires_number || policy.password_requires_number || policy.requires_digit || policy.require_digit),
    require_special_char: isTrue(policy.require_special_char || policy.requires_special_char || policy.requires_special || policy.password_requires_special || policy.require_special),
  };

  const hasUpper = /[A-Z]/.test(safePassword);
  const hasLower = /[a-z]/.test(safePassword);
  const hasNumber = /[0-9]/.test(safePassword);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(safePassword);

  // Build requirements list — only requirements that are explicitly TRUE in the policy are active.
  const requirements = [];
  
  if (p.min_length > 0) {
    requirements.push({
      label: `At least ${p.min_length} characters`,
      met: safePassword.length >= p.min_length,
      active: true,
    });
  }
  if (p.require_number) {
    requirements.push({
      label: "Number",
      met: hasNumber,
      active: true,
    });
  }
  if (p.require_uppercase) {
    requirements.push({
      label: "Uppercase letter",
      met: hasUpper,
      active: true,
    });
  }
  if (p.require_lowercase) {
    requirements.push({
      label: "Lowercase letter",
      met: hasLower,
      active: true,
    });
  }
  if (p.require_special_char) {
    requirements.push({
      label: "Special character",
      met: hasSpecial,
      active: true,
    });
  }

  // Validation state
  const requirementViolations = requirements.filter(r => !r.met).map(r => r.label);
  const isValid = requirementViolations.length === 0;
  const errorMessage = isValid ? null : `Password must include: ${requirementViolations.join(", ")}.`;

  // Score calculation based strictly on satisfied required rules
  const metCount = requirements.filter(r => r.met).length;
  // If no password entered, score is always 0 even if no requirements exist
  const score = (requirements.length > 0 && safePassword.length > 0) ? Math.floor((metCount / requirements.length) * 100) : 0;

  return {
    score,
    strength: getStrengthLabel(score, isValid),
    requirements,
    isValid,
    errorMessage,
  };
};

const getStrengthLabel = (score, isValid) => {
  if (score === 0) return "Very Weak";
  if (!isValid) {
    if (score <= 50) return "Weak";
    if (score <= 75) return "Fair";
    return "Good";
  }
  return "Strong"; // Only strong if 100% valid
};

export const strengthColors = {
  "Very Weak": "bg-red-500",
  "Weak": "bg-orange-500",
  "Fair": "bg-yellow-500",
  "Good": "bg-blue-500",
  "Strong": "bg-green-500",
};
